import { Component, OnInit, ViewEncapsulation } from "@angular/core";
import { HttpClient } from "@angular/common/http";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";
import { EntityState } from "breeze-client";
import _ from "lodash";
import { firstValueFrom } from "rxjs";
import { environment } from "environments/environment";
import { ProductFinderDialogComponent } from "../inventory/product-finder-dialog.component";
import {
  BarcodePrintStatusEnum,
  ItemBin,
  ItemDetail,
  ItemDetailStatusEnum,
  Product,
  ReturnRequestDetail,
} from "../model/entities/entity-model";
import { AddonUtilsService } from "../shared/addon-utils.service";
import { BarcodeService } from "../shared/barcode.service";
import { DialogBaseComponent, DialogParams } from "../shared/dialog-base.component";
import { DomainService } from "../shared/domain.service";
import { EntityFinderDialog } from "../shared/entity-finder.dialog";

export interface RouteRrdDialogParams extends DialogParams {
  rrdId: number;
}

/**
 * Pass 3 dialog for the ROUTING phase. Mirrors the po-utils.service.ts
 * `createItemDetailsFromPoDetail` pattern:
 *
 *   1. Allocate qty next ItemDetail ids via getNextItemDetailIds.
 *   2. Build each ItemDetail with uow.createEntity (Breeze entity, attached
 *      to the EntityManager and queued as Added).
 *   3. Mirror the JoDetail addons onto each via
 *      addonUtilsService.createItemDetailAddonsFromJoDetail.
 *   4. Resolve addon bins via addonUtilsService.findOrCreateAddonBins.
 *   5. dbSaveService.saveChanges() — Breeze sends the whole graph; the
 *      save interceptor in UniformaxPersistenceManager automatically
 *      writes the matching ItemDetailLog rows.
 *   6. Call the server's slim RecordRouting endpoint (validate + phase
 *      transition + ReturnRouted JoNote).
 *   7. Print barcodes from the now-attached ItemDetail entities — the
 *      navigation graph (product/manufacturer/addons) is populated because
 *      they're in the EntityManager.
 *
 * If the operator clicks "Change product", the selected substitute is
 * stamped onto the new ItemDetails. Reject never reaches ROUTING.
 */
@Component({
  selector: "app-route-rrd-dialog",
  templateUrl: "./route-rrd-dialog.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class RouteRrdDialogComponent extends DialogBaseComponent<RouteRrdDialogParams> implements OnInit {
  rrd: ReturnRequestDetail | null = null;
  submitting = false;
  errorMessage: string | null = null;

  selectedProduct: Product | null = null;
  originalProduct: Product | null = null;
  originalProductId: number | null = null;

  constructor(
    config: DynamicDialogConfig<RouteRrdDialogParams>,
    dialogRef: DynamicDialogRef,
    domainService: DomainService,
    private http: HttpClient,
    private barcodeService: BarcodeService,
    private addonUtilsService: AddonUtilsService
  ) {
    super(config, dialogRef, domainService);
  }

  async ngOnInit() {
    const r = await this.uow.fetchEntityByKey(ReturnRequestDetail, this.params.rrdId);
    this.rrd = (r?.entity as ReturnRequestDetail) ?? null;
    if (this.rrd?.joDetail) {
      this.originalProductId = this.rrd.joDetail.productId;
      this.originalProduct = await this.dbQueryService.getProductById(this.originalProductId);
      this.selectedProduct = this.originalProduct;
    }
    this.isPageReady = true;
  }

  get destinationLabel(): string {
    switch (this.rrd?.decision) {
      case "AcceptForCredit":       return "Unitec-owned inventory";
      case "RestockToAccountOwned": return "Account-owned inventory";
      default:                      return "(unknown)";
    }
  }

  get isOverridden(): boolean {
    return this.selectedProduct != null
      && this.originalProductId != null
      && this.selectedProduct.id !== this.originalProductId;
  }

  get canSubmit(): boolean {
    return this.rrd != null
      && !this.submitting
      && this.rrd.decision !== "Reject"
      && this.rrd.decision != null
      && this.selectedProduct != null;
  }

  async pickProduct() {
    const params: any = {};
    if (this.selectedProduct?.productType) {
      params.productType = this.selectedProduct.productType;
    }
    const r = await EntityFinderDialog.open(ProductFinderDialogComponent, this, params);
    const picked = _.first(r) as Product | undefined;
    if (picked == null) return;
    this.selectedProduct = picked;
  }

  revertToOriginal() {
    this.selectedProduct = this.originalProduct;
  }

  variantText(p: Product | null): string {
    if (!p) return "";
    return (p as any).getFeatureChoicesExtract?.() ?? "";
  }

  async routeAndPrint() {
    if (!this.canSubmit || !this.rrd || !this.selectedProduct) return;
    this.submitting = true;
    this.errorMessage = null;

    try {
      const rrd = this.rrd;
      const jod = rrd.joDetail;
      const account = jod.joHeader.account;
      const qty = rrd.qty;

      // Destination per Decision.
      const accountId = rrd.decision === "RestockToAccountOwned" ? account.id : null;
      const productId = this.selectedProduct.id;

      // -------- Breeze entity creation (mirrors createItemDetailsFromPoDetail) --------
      const nextIds = await this.dbQueryService.getNextItemDetailIds(qty);
      const accountAddons = await this.dbQueryService.getAddons(jod.joHeader.accountId);

      const newItemDetails: ItemDetail[] = [];
      const now = new Date(Date.now());

      for (let i = 0; i < qty; i++) {
        const struct = {} as ItemDetail;
        struct.id = nextIds[i];
        struct.productId = productId;
        struct.accountId = accountId;
        struct.itemBinId = ItemBin.UnitecDefault;
        struct.itemDetailStatusId = ItemDetailStatusEnum.InInventory;
        struct.barcodePrintStatusId = BarcodePrintStatusEnum.NotYetPrinted;
        struct.crtnTs = now;

        const itd = this.uow.createEntity(ItemDetail, struct, EntityState.Added);
        // Mirror the JoDetail's addons onto the new ItemDetail.
        this.addonUtilsService.createItemDetailAddonsFromJoDetail(jod, itd, accountAddons);
        newItemDetails.push(itd);
      }

      // Resolve/create addon bins across the new ItemDetails.
      await this.addonUtilsService.findOrCreateAddonBins(newItemDetails);

      // -------- Breeze save (interceptor writes ItemDetailLog rows automatically) --------
      const saved = await this.dbSaveService.saveChanges();
      if (!saved) {
        // Validation dialog already shown by dbSaveService.
        this.submitting = false;
        return;
      }

      // -------- Server orchestration: phase transition + ReturnRouted JoNote --------
      const actor = this.authService.getUser()?.initials ?? "SYS";
      const createdIds = newItemDetails.map(x => x.id);
      const url = environment.apiRoot + "breeze/Uniformax/RecordRouting";
      await firstValueFrom(this.http.post(url, {
        rrdId: rrd.id,
        createdItemDetailIds: createdIds,
        overrideProductId: this.isOverridden ? productId : null,
        actor: actor,
      }));

      // -------- Print barcodes from the now-attached entities --------
      await this.barcodeService.printItemDetailBarcodes(newItemDetails);

      this.toastr.success(`RRD #${rrd.id} routed — ${createdIds.length} ItemDetail(s) created, moved to SETTLING`);
      this.dialogRef.close(true);
    } catch (err: any) {
      const msg = err?.error?.error || err?.message || "Failed to route items.";
      this.errorMessage = msg;
      // Don't rejectChanges here: if save succeeded but the RecordRouting
      // call failed, the ItemDetails are already persisted; the operator
      // should be able to retry the routing step without re-creating them.
    } finally {
      this.submitting = false;
    }
  }

  cancel() {
    // If the operator changed their mind before saving, drop any pending
    // entity changes so the EntityManager isn't polluted.
    if (this.dbSaveService.hasChanges()) {
      this.dbSaveService.rejectChanges();
    }
    this.dialogRef.close(false);
  }

  getPngDialogService() {
    return this.domainService.pngDialogService;
  }
}
